import React from "react";
import CornerCell from "./CornerCell";
import ColumnHeader from "./ColumnHeader";
import { DEFAULT_COL_WIDTH } from "../../constants";

/**
 * GridHeader
 * The table's `<thead>` — the corner cell plus one ColumnHeader per
 * visible column.  Sticky-top positioning is handled in CSS; sticky-left
 * for frozen columns is added inline based on `frozenColOffsets`.
 */
const GridHeader = React.forwardRef(function GridHeader({
  maxCols, colWidths, hiddenCols,
  frozenCols, frozenColOffsets,
  selectionType, normalizedSelection,
  isLastVisibleBeforeHidden,
  filters, activeFilterCol, normalizedRows,
  onToggleFilter, onApplyFilter, onCloseFilter,
  onSelectColumn, onExtendColumn, onHeaderContextMenu, onStartResize,
  getSortState, isFilterColumn,
  onSortAsc, onSortDesc, onClearSort, onCustomSort,
}, ref) {
  return (
    <thead ref={ref}>
      <tr>
        <CornerCell />
        {Array.from({ length: maxCols }).map((_, colIndex) => {
          if (hiddenCols.has(colIndex)) return null;
          const isFrozenCol = colIndex < frozenCols;
          // Columns beyond the sheet's parsed widths (revealed by scrolling
          // right) fall back to the default width.
          const cw = colWidths[colIndex] || DEFAULT_COL_WIDTH;
          const className = [
            isLastVisibleBeforeHidden(colIndex) ? "sev-hidden-col-after" : "",
            selectionType === "col" && colIndex >= normalizedSelection.c1 && colIndex <= normalizedSelection.c2
              ? "sev-sel-header" : "",
            colIndex === frozenCols - 1 ? "sev-freeze-colline" : "",
          ].join(" ").trim();

          const style = {
            minWidth: `${cw}px`,
            width: `${cw}px`,
            ...(isFrozenCol
              ? { position: "sticky", left: `${frozenColOffsets[colIndex] || 0}px`, zIndex: 25 }
              : {}),
          };

          return (
            <ColumnHeader
              key={`col-${colIndex}`}
              colIndex={colIndex}
              colWidth={cw}
              className={className}
              style={style}
              isFiltered={Boolean(filters[colIndex])}
              isFilterMenuOpen={activeFilterCol === colIndex}
              filterRows={normalizedRows}
              currentFilter={filters[colIndex]}
              onToggleFilter={onToggleFilter}
              onApplyFilter={onApplyFilter}
              onCloseFilter={onCloseFilter}
              onSelect={onSelectColumn}
              onExtend={onExtendColumn}
              onContextMenu={(e) => onHeaderContextMenu(e, "col", colIndex)}
              onStartResize={onStartResize}
              showDropdown={isFilterColumn ? isFilterColumn(colIndex) : false}
              sortState={getSortState ? getSortState(colIndex) : null}
              onSortAsc={onSortAsc}
              onSortDesc={onSortDesc}
              onClearSort={onClearSort}
              onCustomSort={onCustomSort}
            />
          );
        })}
      </tr>
    </thead>
  );
});

export default React.memo(GridHeader);
